import React, { useEffect, useState, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, useMediaQuery, IconButton, Drawer } from "@mui/material";
import moment from "moment/moment";
import { useLocation } from "react-router-dom";
import ChatInput from "views/Components/ChatInput";
import ChatMessages from "views/Components/ChatMessages";
import CategoryMenu from "views/Components/CategoryMenu";
import localization from "services/localization";
import SpecificAPI from "services/SpecificApi";
import Breadcrumbs from "views/Components/Breadcrumbs ";
import { useUserStore } from "context/useUserStore";
import ChatRankingModal from "views/Components/RankingModal";
import { FormBuilderModal } from "views/Components/FormBuilder/form-builder-modal";
import { usePromptsStore } from "context/usePromptsStore";
import { useDepartmentsStore } from "context/useDepartmentsStore";
import { useWebSocket } from "context/useWebsocketStore";
import { BookOpen, InfoIcon } from "lucide-react";
import { useLanguageStore } from "context/useLanguageStore";

import "../../css/dashboard.css";

const DISCUSSION_FORM_FIELDS = [
  {
    inputType: "textarea",
    name: "message",
    label: "Message",
    description: "Discussion Message",
    placeholder: "Write your text here...",
    required: false,
  },
  {
    inputType: "date",
    name: "start_date",
    label: "Start Date",
    description: "Select discussion start date",
    required: false,
  },
  {
    inputType: "date",
    name: "end_date",
    label: "End Date",
    description: "Select discussion end date",
    required: false,
  },
  {
    inputType: "file-uploader",
    name: "resource",
    label: "Resources",
    description: "Upload additional resource",
    required: false,
  },
  {
    inputType: "student-list",
    name: "students",
    label: "Students",
    description: "Select Student List",
    options: [
      { key: "student-1", label: "Alice Johnson" },
      { key: "student-2", label: "Bob Williams" },
      { key: "student-3", label: "Carol Davis" },
      { key: "student-4", label: "David Brown" },
    ],
    required: false,
  },
];

const ChatDashboard = () => {
  const { currentLang } = useLanguageStore();
  const isTablet = useMediaQuery("(max-width: 1224px)");
  const { moduleId, departmentId, historyId } = useParams();

  const navigate = useNavigate();
  const [openChatRankingModal, setOpenChatRankingModal] = useState(false);
  const [scrollToMessageId, setScrollToMessageId] = useState(null);

  const [language, setLanguage] = useState(localStorage.getItem("SA_user_lang") || "En");
  const [collapsed, setCollapsed] = useState(isTablet);
  const messagesContainerRef = useRef(null);
  const [botMode, setBotMode] = useState("Free");
  const [messages, setMessages] = useState([]);
  const [selectedHistory, setSelectedHistory] = useState(null);
  const [messagesList, setMessagesList] = useState([]);
  const [theme, setTheme] = useState(localStorage.getItem("SA_user_theme") || "dark");
  const [selectedPromptData, setSelectedPromptData] = useState(null);
  const [showPromptModal, setShowPromptModal] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [selectedHistoryId, setSelectedHistoryId] = useState(null);
  const [students, setStudents] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [canUserChat, setCanUserChat] = useState(true);
  const [loading, setLoading] = useState(false);
  const [messageUploadedLoading, setMessageUploadedLoading] = useState(false);
  const [rankingContext, setRankingContext] = useState({
    type: "all",
    userId: null,
  });
  const [summaryMessages, setSummaryMessages] = useState([]);

  const { authUser, appUser, userData, setUserAppData } = useUserStore();

  const { prompts, setPrompts } = usePromptsStore();
  const { departments } = useDepartmentsStore();
  const location = useLocation();
  const hasTempMessageRef = useRef(false);

  const [totalCredits, setTotalCredits] = useState(0);
  const [creditsUsed, setCreditsUsed] = useState(0);
  const [history_is_disabled, setHistoryIsDisabled] = useState(false);

  const role = appUser?.role || null;

  const { subscribe, leave, isReady } = useWebSocket();

  useEffect(() => {
    // console.log("botMode", botMode);
    if (botMode == "Searching") {
      setTimeout(() => {
        setBotMode("Reasoning");
      }, 3000);
    } else if (botMode == "Reasoning") {
      setTimeout(() => {
        setBotMode("Free");
        handleGenerateResponse();
      }, 3000);
    }
  }, [botMode]);

  useEffect(() => {
    setUserAppData({
      ...userData,
      selectedHistoryMemberMessageCount: messages.filter((msg) => msg.user_id !== appUser?.id).length,
      selectedHistoryOwnerMessageCount: messages.filter((msg) => msg.user_id == appUser?.id).length,
    });
  }, [messages]);

  const isChatHistory = useMemo(() => {
    if (!selectedHistory) return false;
    return selectedHistory?.prompt?.prompt_template?.type === "chat";
  }, [selectedHistory]);

  useEffect(() => {
    if (!historyId) return;
    const tempMessage = location.state?.tempMessage;
    const messageType = tempMessage?.type || selectedHistory?.prompt?.prompt_template?.type;

    const isChatHistory1 = messageType === "chat";

    if (tempMessage && !isChatHistory1) {
      hasTempMessageRef.current = true;
      setMessages([tempMessage]);

      // send actual message to API
      SpecificAPI.createMessage(
        {
          user_id: tempMessage.user_id,
          prompt_id: tempMessage.prompt_id,
          history_id: tempMessage.history_id,
          question: tempMessage.question,
          saved: tempMessage.saved,
          timestamp: tempMessage.timestamp,
        },
        userData?.selected_school_id,
      )
        .then((res) => {
          const savedMsg = res.data;
          setMessages((prev) => prev.map((m) => (m.id === tempMessage.id ? savedMsg : m)));

          hasTempMessageRef.current = false;
          console.log("updateHistoryTitle called for auto first message", savedMsg);
          updateHistoryTitle(savedMsg);
        })
        .catch((err) => {
          console.error("Failed to send message", err);
          setMessages((prev) =>
            prev.map((m) =>
              m.id === tempMessage.id ? { ...m, loading: false, response: "Error sending message" } : m,
            ),
          );
          hasTempMessageRef.current = false;
        });
    } else {
      hasTempMessageRef.current = false;
    }
  }, [historyId]);

  useEffect(() => {
    if (!historyId) return;
    setSelectedHistoryId(historyId);
    getHistoryData(historyId);
  }, [historyId]);

  useEffect(() => {
    getAllData();
  }, []);

  useEffect(() => {
    console.log("received updatedHistory", userData?.updatedHistory);

    if (!userData?.updatedHistory) return;

    const { id, datetime_start_playground, datetime_end_playground } = userData.updatedHistory;

    if (!id || !datetime_start_playground || !datetime_end_playground) return;

    if (selectedHistory?.id === id) {
      console.log("updated", userData.updatedHistory);

      setSelectedHistory((prev) => {
        const newHistory = {
          ...prev,
          prompt_data: {
            ...prev.prompt_data,
            datetime_start_playground,
            datetime_end_playground,
          },
        };

        setHistoryIsDisabled(isOutsideDateRange(newHistory));

        return newHistory;
      });
    }

    setUserAppData({
      ...userData,
      updatedHistory: null,
    });
  }, [userData?.updatedHistory]);

  const updateHistoryTitle = (message) => {
    console.log("updateHistoryTitle inside function", message);
    SpecificAPI.updateHistory(
      message?.history_id,
      { title: message?.prompt_health?.title },
      userData?.selected_school_id,
    )
      .then((res) => {
        setUserAppData({
          ...userData,
          newHistoryTitle: {
            id: message?.history_id,
            title: message?.prompt_health?.title,
          },
        });
      })
      .catch((err) => {
        console.error("Failed to update history title", err);
      });
  };

  const getAllData = async () => {
    if (!appUser) return;

    try {
      const studentsRes = await SpecificAPI.getAllUsers({
        role: "student",
      });

      let filteredStudents = studentsRes.filter((s) => s?.school_id?.includes(userData?.selected_school_id));

      setStudents(filteredStudents || []);
    } catch (error) {
      console.error("Error retrieving students:", error);
    }
  };

  // const pullMessages = async (historyId) => {
  //   if (!historyId) return;

  //   try {
  //     const messagesRes = await SpecificAPI.getAllMessages(
  //       {
  //         history_id: historyId,
  //       },
  //       userData?.selected_school_id,
  //     );

  //     let finalMessages = messagesRes || [];

  //     // if (appUser?.role === "student") {
  //     //   const userMessages = messagesRes.filter(
  //     //     (m) => m.user_id === appUser.id,
  //     //   );
  //     //   const firstMessage = messagesRes.filter(
  //     //     (m) => m.user_id === history.user_id,
  //     //   );

  //     //   finalMessages = [...firstMessage, ...userMessages];
  //     //   console.log(messagesRes,userMessages,finalMessages);
  //     // }
  //     setMessages(finalMessages);
  //   } catch (err) {
  //     console.error("Failed to load history messages:", err);
  //   }
  // };

  // useEffect(() => {
  //   if (!historyId) return;

  //   const interval = setInterval(() => {
  //     if (!hasTempMessageRef.current) {
  //       pullMessages(historyId);
  //     }
  //   }, 10000);

  //   return () => clearInterval(interval);
  // }, [historyId]);

  useEffect(() => {
    if (!isReady || !historyId || !appUser) return;

    if (appUser.role === "teacher") {
      subscribe(`history.${historyId}`, ".new.message", (data) => {
        if (data.message.user_id !== appUser.id) {
          console.log("new message updated");
          setMessages((prev) => [...prev, data.message]);
        }
      });
    }

    return () => leave(`history.${historyId}`);
  }, [isReady, historyId, appUser]);

  const localizedPromptTitle = useMemo(() => {
    if (!selectedHistory?.prompt?.title) return "";

    const titles = selectedHistory.prompt.title;

    return titles[currentLang] || titles["It"] || Object.values(titles)[0] || "";
  }, [selectedHistory, currentLang]);

  const welcomeMessageText = useMemo(() => {
    if (!selectedHistory) return "";

    const welcomeBase = localization.welcome_to_chat || "Welcome to the discussion";

    const instruction =
      localization.welcome_instruction || "Use this space to explore the topic and ask questions about";

    const topic = selectedHistory?.prompt_data?.argomento_principale?.trim() || localizedPromptTitle;

    if (!topic) return `${welcomeBase}. ${instruction}`;

    return `${instruction}: ${topic}`;
  }, [selectedHistory, localizedPromptTitle]);

  const isOutsideDateRange = (history) => {
    if (!history?.prompt_data?.datetime_start_playground || !history?.prompt_data?.datetime_end_playground)
      return false;

    const now = new Date();
    const start = new Date(history?.prompt_data?.datetime_start_playground);
    const end = new Date(history?.prompt_data?.datetime_end_playground);
    return now < start || now > end;
  };

  const getHistoryData = async (historyId) => {
    setLoading(true);

    try {
      const history = await SpecificAPI.getHistoryById(historyId, userData?.selected_school_id);

      if (!history) return;

      setSelectedHistory(history);

      if (!hasTempMessageRef.current) {
        getHistoryMessages(historyId, history.prompt_id);
      }

      setCanUserChat(history?.prompt?.prompt_template?.system_props?.can_user_chat ?? true);

      setTotalCredits(history?.prompt_data?.max_students_interactions || 0);

      if (isOutsideDateRange(history)) {
        setHistoryIsDisabled(true);
      } else {
        setHistoryIsDisabled(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const getHistoryMessages = async (historyId, promptId) => {
    try {
      setMessagesLoading(true);
      setMessages([]);

      const messagesRes = await SpecificAPI.getAllMessages(
        {
          history_id: historyId,
        },
        userData?.selected_school_id,
      );

      let finalMessages = messagesRes || [];

      if (appUser?.role === "student") {
        const userMessages = messagesRes.filter((m) => m.user_id === appUser.id);

        setCreditsUsed(userMessages.length);
      }

      // if (appUser?.role === "student") {
      //   const userMessages = messagesRes.filter(
      //     (m) => m.user_id === appUser.id,
      //   );
      //   const firstMessage = messagesRes.filter(
      //     (m) => m.user_id === history.user_id,
      //   );

      //   finalMessages = [...firstMessage, ...userMessages];
      // }

      setMessages(finalMessages.filter((m) => m.prompt_id === promptId));
      setSummaryMessages(finalMessages.filter((m) => m.prompt_id !== promptId));

      // if (history.prompt_id && appUser?.role != "student") {
      //   const promptData = await SpecificAPI.getPromptById(history.prompt_id);
      //   setSelectedPrompt(promptData);

      //   if (promptData?.prompt_template_id) {
      //     const templateRes = await SpecificAPI.getPromptTemplateById(
      //       promptData.prompt_template_id,
      //     );

      //     const canUserChat = templateRes?.system_props?.can_user_chat ?? true;
      //     setShowMessageInput(canUserChat);
      //   }
      // } else {
      //   setShowMessageInput(true);
      // }
    } catch (err) {
      console.error("Failed to load history messages:", err);
    } finally {
      setMessagesLoading(false);
    }
  };

  // useEffect(() => {
  //   scrollToBottom();
  // }, [messages]);

  // const scrollToBottom = () => {
  //   if (messagesContainerRef.current) {
  //     messagesContainerRef.current.scrollTop =
  //       messagesContainerRef.current.scrollHeight;
  //   }
  // };

  const scrollToBottom = (force = false) => {
    if (!messagesContainerRef.current) return;

    if (force) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  const rankingMessages = useMemo(() => {
    if (rankingContext.type === "student" && rankingContext.userId) {
      return messages.filter((m) => m.user_id === rankingContext.userId);
    }
    return messages;
  }, [messages, rankingContext]);

  const openGlobalRanking = (show) => {
    setRankingContext({ type: "all", userId: null });
    setOpenChatRankingModal(show);
    setCollapsed(show);
  };

  const openStudentRanking = (studentId) => {
    setRankingContext({ type: "student", userId: studentId });
    setOpenChatRankingModal(true);
    setCollapsed(true);
  };
  const toggleSidebar = () => {
    setCollapsed(!collapsed);
  };

  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    localStorage.setItem("SA_user_lang", lang);
    localization.setLanguage(lang);
    window.dispatchEvent(new Event("storage"));
  };

  const handleDarkModeChange = (event) => {
    const checked = event.target.checked;
    setDarkMode(checked);

    if (checked) {
      document.documentElement.setAttribute("data-theme", "dark");
      localStorage.setItem("SA_user_theme", "dark");
      setTheme("dark");
    } else {
      document.documentElement.setAttribute("data-theme", "light");
      localStorage.setItem("SA_user_theme", "light");
      setTheme("light");
    }
  };

  const addUserMessage = async (message, retryCount = 0, tempId = null, tempMsg = null) => {
    if (!selectedHistoryId) {
      console.error("No selected history. Cannot send message.");
      return;
    }
    const currentTempId = tempId || `temp-${Date.now()}`;
    let currentTempMsg = tempMsg;
    if (!tempId) {
      currentTempMsg = {
        id: currentTempId,
        user_id: appUser.id,
        history_id: selectedHistoryId,
        prompt_id: selectedHistory.prompt_id,
        question: message,
        response: null,
        saved: false,
        created_at: moment().toISOString(),
        loading: true,
      };
      setMessages((prev) => [...prev, currentTempMsg]);
    }

    try {
      setMessageUploadedLoading(true);
      hasTempMessageRef.current = true;

      const response = await SpecificAPI.createMessage(
        {
          ...currentTempMsg,
        },
        userData?.selected_school_id,
      );
      // const response = await (async () => {
      //   throw new Error("Test error");
      // })();

      // if(messages.length==0){
      //   console.log("updateHistoryTitle called for new message");
      //   updateHistoryTitle(response.data);
      // }

      const savedMsg = response.data || {
        ...currentTempMsg,
        loading: false,
        response: "No response",
      };

      // Replace temp message with real API response
      setMessages((prev) => prev.map((m) => (m.id === currentTempId ? savedMsg : m)));

      if (appUser?.role == "student") {
        setCreditsUsed((cre) => cre + 1);
      }
    } catch (error) {
      console.error("Error sending user message:", error?.error?.response?.data || error?.message);

      // Optionally mark message as error
      if (retryCount < 3) {
        addUserMessage(message, retryCount + 1, currentTempId, currentTempMsg);
      } else {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === tempId
              ? {
                  ...m,
                  loading: false,
                  response: error?.error?.response?.data || error?.message || "Error sending message",
                }
              : m,
          ),
        );
      }
    } finally {
      hasTempMessageRef.current = false;
      setMessageUploadedLoading(false);
      scrollToBottom(true);
    }
  };
  const handleGenerateResponse = () => {
    const response = "Sure! Today, we will cover an introduction to algebra focusing on solving simple equations.";
    setMessages((prevMessages) => [
      ...prevMessages,
      {
        type: "bot",
        content: "",
        historyId: selectedHistoryId,
        saved: false,
        timestamp: moment().toISOString(),
      },
    ]);

    const words = response.split(" ");
    let index = 0;

    const typeMessage = () => {
      setMessages((prevMessages) => {
        const updatedMessages = [...prevMessages];
        const lastIndex = updatedMessages.length - 1;

        updatedMessages[lastIndex] = {
          ...updatedMessages[lastIndex],
          content: updatedMessages[lastIndex].content + (index > 0 ? " " : "") + words[index],
        };

        return updatedMessages;
      });

      index++;

      if (index < words.length - 1) {
        setTimeout(typeMessage, 50);
      }
    };

    typeMessage();
  };

  const updateSavedMessages = (historyId, msg) => {
    const updatedMessages = msg;
    updatedMessages.saved = !updatedMessages.saved;
    const updatedMessagesList = messagesList.map((m) => {
      if (m.id === historyId) {
        return {
          ...m,
          saved: !m.saved,
        };
      }
      return m;
    });

    setMessagesList(updatedMessagesList);
    // setHistoryList((prevHistoryList) =>
    //     prevHistoryList.map((chat) =>
    //         chat.id === historyId
    //             ? {
    //                 ...chat,
    //                 messages: chat.messages.map((msg) =>
    //                     msg.id === msg.id
    //                         ? { ...msg, saved: !msg.saved }
    //                         : msg
    //                 ),
    //             }
    //             : chat
    //     )
    // );
    // return new Promise((resolve, reject) => {
    //     SpecificAPI.updateMessages(updatedMessages)
    //         .then((result) => {
    //             resolve(true);
    //         })
    //         .catch((error) => {
    //             console.error("Error updating favorite prompt: ", error);
    //             reject(error);
    //         });
    // });
  };

  const toggleFavoritePrompt = (id) => {
    const updatedPromptList = prompts.map((item) =>
      item.id === id ? { ...item, isFavorite: !item.isFavorite } : item,
    );

    const updatedPrompt = updatedPromptList.find((item) => item.id === id);

    return new Promise((resolve, reject) => {
      SpecificAPI.updatePrompt(updatedPrompt)
        .then((result) => {
          setPrompts(updatedPromptList);
          resolve(true);
        })
        .catch((error) => {
          console.error("Error updating favorite prompt: ", error);
          reject(error);
        });
    });
  };

  const openPromptModal = async (data) => {
    setSelectedPromptData(data);
    const templateId = data?.prompt_template_id;
    const response = await SpecificAPI.getPromptTemplateById(templateId);
    const apiFields = response?.system_props?.viz_props;
    if (apiFields == null || apiFields.length == 0) {
      addPromptMessage({}, null, data.id, response?.system_props?.chat_visible_to_members);
    } else {
      setShowPromptModal(true);
    }
  };

  const handleMessageStop = () => {
    // console.log("here");
  };

  const handleSudentSelect = (history) => {
    var messages = messagesList.filter((m) => m.historyId === history.id);
    setMessages(messages);
  };

  const addPromptMessage = async (formData, selectedAttachment, promptId, chat_visible_to_members) => {
    try {
      setLoading(true);
      const isDiscussionTopic =
        selectedPromptData?.title?.En === "Discussion and in-depth study" ||
        selectedPromptData?.title?.It === "Discussione e approfondimento";

      // 1. Create history
      const newHistory = {
        userId: appUser.id,
        title: selectedPromptData.title[currentLang],
        privacy: chat_visible_to_members ? "public" : "private",
        prompt_id: promptId,
        promptData: {
          ...formData,
          resouce_id: selectedAttachment || null,
        },
        department_id: departmentId,
      };

      const response = await SpecificAPI.createHistory(newHistory, userData?.selected_school_id);
      const createdHistoryId = response?.data?.id;

      if (!createdHistoryId) {
        console.error("Failed to create history: No ID returned");
        return;
      }

      // 2. Create initial message BEFORE navigating
      const msg = {
        user_id: appUser.id,
        prompt_id: selectedPromptData.id,
        history_id: createdHistoryId,
        question: formData.message,
        saved: false,
        timestamp: moment().toISOString(),
      };

      try {
        await SpecificAPI.createMessage(msg, userData?.selected_school_id);
      } catch (msgError) {
        console.error("Error sending initial message:", msgError);
      }

      // 3. Navigate AFTER message creation
      navigate(`/edu-prompt/history/module/${moduleId}/${departmentId}/${createdHistoryId}`);
    } catch (error) {
      console.error("Error creating history:", error);
    } finally {
      setLoading(false);
    }
  };
  const handleShowRankingModal = (show) => {
    setOpenChatRankingModal(show);
    setCollapsed(show);
  };

  const handleGoToChat = (id) => {
    // console.log(`Navigating to chat: ${id}`);
    setScrollToMessageId(id);
  };

  const canShowCategoryMenu = role === "teacher" && selectedHistory?.privacy === "public";

  const categoryMenu = (
    <CategoryMenu
      openPromptModal={openPromptModal}
      promptList={prompts}
      favoritePromptList={prompts.filter((p) => p.isFavorite)}
      toggleFavoritePrompt={toggleFavoritePrompt}
      role={role}
      classroomId={departmentId}
      students={students}
      history={selectedHistory}
      openStudentRanking={openStudentRanking}
      messages={messages}
      selectedHistory={selectedHistory}
      isTablet={isTablet}
    />
  );

  const isProcessing = messages.some((m) => m.loading);
  return (
    <div className="flex flex-col h-[calc(100vh-70px)]">
      <Breadcrumbs
        role={role}
        language={currentLang}
        selectedPrompt={selectedPrompt}
        moduleId={moduleId}
        departmentId={departmentId}
        selectedHistory={selectedHistory}
      />
      <div className="flex flex-1 overflow-hidden">
        <div className="flex flex-col flex-1 overflow-hidden">
          {/* Chat messages */}
          {isChatHistory && localizedPromptTitle && (
            <div className="px-4 pt-2">
              <div
                className="welcome-message flex items-start gap-3
                    px-5 py-4 rounded-2xl 
                    border border-slate-200 dark:border-slate-700 
                    shadow-sm"
              >
                <div className="mt-1">
                  <BookOpen className="w-5 h-5 icon-btn" />
                </div>

                <div>
                  <p className="text-sm font-semibold tracking-wide">{welcomeMessageText}</p>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesContainerRef} className="flex-1 min-h-0 overflow-y-auto p-4 pt-2 pb-2 custom-scroll">
            <ChatMessages
              theme={theme}
              messages={messages}
              botMode={botMode}
              updateSavedMessages={updateSavedMessages}
              scrollToMessageId={scrollToMessageId}
              setMessages={setMessages}
              role={role}
              students={students}
              messagesLoading={messagesLoading}
              messageUploadedLoading={messageUploadedLoading}
              welcomeMessageText={welcomeMessageText}
              currentHistory={selectedHistory}
              loading={loading}
            />
          </div>

          {canUserChat && (
            <div className="sticky bottom-0 p-4 pt-2 pb-2 bg-[var(--dashboard-bg)]">
              <ChatInput
                showRankingModal={openGlobalRanking}
                totalCredits={totalCredits}
                creditsUsed={creditsUsed}
                history_is_disabled={history_is_disabled}
                onSendMessage={addUserMessage}
                theme={theme}
                onStop={handleMessageStop}
                messageUploadedLoading={isProcessing}
                localizedPromptTitle={localizedPromptTitle}
              />
            </div>
          )}
          <div className="flex items-center justify-center gap-1 pt-2 text-gray-500">
            <InfoIcon size={14} />

            <p className="text-xs font-light leading-relaxed text-center">
              {localization.input_disclaimer ||
                "Le risposte generate dall'IA possono contenere errori, bias o informazioni inventate. Verifica sempre con fonti autorevoli prima di utilizzare le risposte."}
            </p>
          </div>
        </div>
        {canShowCategoryMenu && (
          <CategoryMenu
            openPromptModal={openPromptModal}
            promptList={prompts}
            favoritePromptList={prompts.filter((p) => p.isFavorite)}
            toggleFavoritePrompt={toggleFavoritePrompt}
            role={role}
            classroomId={departmentId}
            students={students}
            history={selectedHistory}
            openStudentRanking={openStudentRanking}
            messages={messages}
            selectedHistory={selectedHistory}
            isTablet={isTablet}
          />
        )}
      </div>

      <FormBuilderModal
        isOpen={showPromptModal}
        onClose={() => setShowPromptModal(false)}
        fields={DISCUSSION_FORM_FIELDS}
        title="Create Discussion"
        description="Set up a new discussion with students"
        onSubmit={addPromptMessage}
        submitButtonLabel="Create"
        students={students}
      />

      {openChatRankingModal && (
        <ChatRankingModal
          open={openChatRankingModal}
          darkMode={theme == "dark"}
          messages={rankingMessages}
          summaryMessages={summaryMessages}
          setSummaryMessages={setSummaryMessages}
          historyId={historyId}
          onOpenChange={(open) => handleShowRankingModal(open)}
          onGoToChat={handleGoToChat}
        />
      )}
    </div>
  );
};

export default ChatDashboard;
